import React, { useEffect, useState } from "react";
import { ArrowRightLeft, Building2, Check, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Panel, Empty } from "@/pages/shared";

const blank = () => ({ name: "", domain: "", email_domain: "", slug: "", admin_email: "", active: true, twofa_sms: false, turnstile_enabled: true, demo_allowed: false });

const YesNo = ({ on, goodWhenOn, yes, no, onClick, label }) => {
  const good = on === goodWhenOn;
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`text-[11px] font-semibold underline-offset-2 hover:underline ${good ? "text-emerald-600" : "text-rose-600"}`}
    >
      {on ? yes : no}
    </button>
  );
};

export default function TenantsPanel() {
  const { t } = useLang();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editSlug, setEditSlug] = useState(null);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [handover, setHandover] = useState(null);  // camping waarvan het webadres verhuist
  const [target, setTarget] = useState("");

  const load = async () => {
    try { setRows((await api.get("/tenants")).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, admin_email: form.admin_email || null };
      if (editSlug) await api.put(`/tenants/${editSlug}`, body);
      else await api.post("/tenants", body);
      setOpen(false);
      await load();
      toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resetDemo = async () => {
    if (!window.confirm(t("demo_reset_confirm"))) return;
    setBusy(true);
    try {
      await api.post("/demo/reset");
      await load();
      toast.success(t("demo_reset_done"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doHandover = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${handover.id}/handover`, { target });
      setHandover(null);
      await load();
      toast.success(t("t_handover_ok").replace("{domains}", data.moved.join(", ")));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(t("t_delete_confirm").replace("{name}", row.name))) return;
    setBusy(true);
    try {
      await api.delete(`/tenants/${row.id}`);
      await load();
      toast.success(t("t_delete_ok").replace("{name}", row.name));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleFlag = async (row, field, next, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.patch(`/tenants/${row.id}/flag`, { field, value: next });
      await load();
      toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleDemo = async (row) => {
    const next = !(row.demo_allowed === true);
    let msg = t("flag_confirm").replace("{flag}", t("t_demo")).replace("{name}", row.name)
      .replace("{state}", next ? t("flag_on") : t("flag_off"));
    if (!next) {
      try {
        const { data } = await api.get(`/tenants/${row.id}/demo-count`);
        if (data.total > 0) msg = t("demo_off_purge_confirm").replace("{n}", data.total).replace("{name}", row.name);
      } catch (e) { /* telling mislukt — val terug op standaardvraag */ }
    }
    await toggleFlag(row, "demo_allowed", next, msg);
  };

  const switchTo = async (slug) => {    try {
      await api.post(`/tenants/${slug}/switch`);
      toast.success(t("switched"));
      window.location.href = "/";
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Panel
      title={t("tenants")}
      testid="tenants-panel"
      action={<Button onClick={() => { setForm(blank()); setEditSlug(null); setOpen(true); }} data-testid="add-tenant-button"><Plus className="h-4 w-4 mr-1" />{t("new_tenant")}</Button>}
    >
      <p className="px-5 pt-4 text-sm text-muted-foreground">{t("tenants_desc")}</p>
      {rows.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("t_domain")}</TableHead>
              <TableHead>{t("t_name")}</TableHead>
              <TableHead>{t("t_email_domain")}</TableHead>
              <TableHead className="text-center">{t("t_twofa_short")}</TableHead>
              <TableHead className="text-center">{t("t_turnstile_short")}</TableHead>
              <TableHead className="text-center">{t("t_demo_short")}</TableHead>
              <TableHead>{t("t_slug")}</TableHead>
              <TableHead className="text-right">{t("t_users")}</TableHead>
              <TableHead className="text-right">{t("t_pitches")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`tenant-row-${r.id}`} className={r.active ? "" : "opacity-50"}>
                <TableCell className="font-medium">{(r.domains?.length ? r.domains : [r.domain]).filter(Boolean).join(", ") || "—"}</TableCell>
                <TableCell className="font-semibold text-foreground">
                  {r.name}
                  {r.id === user?.tenant_id && <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600"><Check className="h-3 w-3" />{t("active_camp")}</span>}
                </TableCell>
                <TableCell className="text-muted-foreground">{r.email_domain || "—"}</TableCell>
                <TableCell className="text-center" data-testid={`tenant-twofa-${r.id}`}><YesNo on={!!r.twofa_sms} goodWhenOn={true} yes={t("yes")} no={t("no")} label={t("t_twofa")} onClick={() => toggleFlag(r, "twofa_sms", !r.twofa_sms, t("flag_confirm").replace("{flag}", t("t_twofa")).replace("{name}", r.name).replace("{state}", !r.twofa_sms ? t("flag_on") : t("flag_off")))} /></TableCell>
                <TableCell className="text-center" data-testid={`tenant-turnstile-${r.id}`}><YesNo on={r.turnstile_enabled !== false} goodWhenOn={true} yes={t("yes")} no={t("no")} label={t("t_turnstile")} onClick={() => toggleFlag(r, "turnstile_enabled", r.turnstile_enabled === false, t("flag_confirm").replace("{flag}", t("t_turnstile")).replace("{name}", r.name).replace("{state}", r.turnstile_enabled === false ? t("flag_on") : t("flag_off")))} /></TableCell>
                <TableCell className="text-center" data-testid={`tenant-demo-${r.id}`}><YesNo on={r.demo_allowed === true} goodWhenOn={false} yes={t("yes")} no={t("no")} label={t("t_demo")} onClick={() => toggleDemo(r)} /></TableCell>
                <TableCell className="text-xs uppercase tracking-wide text-muted-foreground">{r.id}</TableCell>
                <TableCell className="text-right tabular-nums">{r.users}</TableCell>
                <TableCell className="text-right tabular-nums">{r.pitches}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => { setForm({ ...blank(), ...r, slug: r.id, domain: (r.domains?.length ? r.domains : [r.domain]).filter(Boolean).join(", ") }); setEditSlug(r.id); setOpen(true); }} data-testid={`edit-tenant-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="outline" size="sm" className="ml-1" disabled={r.id === user?.tenant_id} onClick={() => switchTo(r.id)} data-testid={`switch-tenant-${r.id}`}>
                    <Building2 className="h-4 w-4 mr-1" />{t("switch_tenant")}
                  </Button>
                  {r.id === "campad-demo" && (
                    <Button variant="outline" size="sm" className="ml-1" disabled={busy} onClick={resetDemo} data-testid="reset-demo-button">
                      <RotateCcw className="h-4 w-4 mr-1" />{t("demo_reset")}
                    </Button>
                  )}
                  {r.id !== user?.tenant_id && r.users === 0 && r.pitches === 0 && (r.domains?.length || r.domain) && (
                    <Button variant="outline" size="sm" className="ml-1" disabled={busy}
                      onClick={() => { setHandover(r); setTarget(rows.find((x) => x.id !== r.id && x.active)?.id || ""); }}
                      data-testid={`handover-tenant-${r.id}`}>
                      <ArrowRightLeft className="h-4 w-4 mr-1" />{t("t_handover")}
                    </Button>
                  )}
                  {r.id !== user?.tenant_id && r.users === 0 && r.pitches === 0 && (
                    <Button variant="ghost" size="sm" className="ml-1 text-rose-600 hover:text-rose-700" disabled={busy}
                      onClick={() => remove(r)} data-testid={`delete-tenant-${r.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!handover} onOpenChange={(v) => !v && setHandover(null)}>
        <DialogContent data-testid="handover-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("t_handover")} — {handover?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t("t_handover_desc")
                .replace("{domains}", (handover?.domains?.length ? handover.domains : [handover?.domain]).filter(Boolean).join(", "))
                .replace("{name}", handover?.name || "")}
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("t_handover_target")}</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="handover-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {rows.filter((x) => x.id !== handover?.id && x.active).map((x) => (
                    <SelectItem key={x.id} value={x.id} data-testid={`handover-option-${x.id}`}>{x.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHandover(null)} data-testid="handover-cancel">{t("cancel")}</Button>
            <Button onClick={doHandover} disabled={busy || !target} data-testid="handover-confirm">{t("t_handover_do")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tenant-dialog">
          <DialogHeader><DialogTitle className="font-display">{editSlug ? `${t("edit")} — ${form.name}` : t("new_tenant")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("t_domain")}</Label>
              <p className="text-[11px] text-muted-foreground">{t("t_domain_hint")}</p>
              <Input value={form.domain} placeholder="cap-blanch.pro, test.cap-blanch.pro" onChange={(e) => setForm({ ...form, domain: e.target.value })} data-testid="tenant-domain" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("t_name")}</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tenant-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("t_email_domain")}</Label>
              <Input value={form.email_domain} placeholder="@camping-capblanch.com" onChange={(e) => setForm({ ...form, email_domain: e.target.value })} data-testid="tenant-email-domain" />
            </div>
            {!editSlug && (
              <div className="space-y-1.5">
                <Label className="text-xs">{t("t_slug")}</Label>
                <Input value={form.slug} placeholder={t("t_slug_hint")} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="tenant-slug" />
                <p className="text-[11px] text-muted-foreground">{t("t_slug_fixed")}</p>
              </div>
            )}
            {!editSlug && (
              <div className="space-y-1.5">
                <Label className="text-xs">{t("t_admin_email")}</Label>
                <Input value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="tenant-admin-email" />
                <p className="text-[11px] text-muted-foreground">{t("user_pw_info")}</p>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="tenant-active" />
              {t("active")}
            </label>
            <div className="rounded-lg border border-border bg-secondary/30 px-3 py-2 space-y-1">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={!!form.twofa_sms} onChange={(e) => setForm({ ...form, twofa_sms: e.target.checked })} data-testid="tenant-twofa" />
                {t("t_twofa")}
              </label>
              <p className="text-[11px] text-muted-foreground">{t("t_twofa_hint")}</p>
            </div>
            <div className="rounded-lg border border-border bg-secondary/30 px-3 py-2 space-y-1">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={form.turnstile_enabled !== false} onChange={(e) => setForm({ ...form, turnstile_enabled: e.target.checked })} data-testid="tenant-turnstile" />
                {t("t_turnstile")}
              </label>
              <p className="text-[11px] text-muted-foreground">{t("t_turnstile_hint")}</p>
            </div>
            <div className="rounded-lg border border-border bg-secondary/30 px-3 py-2 space-y-1">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={!!form.demo_allowed} onChange={(e) => setForm({ ...form, demo_allowed: e.target.checked })} data-testid="tenant-demo-allowed" />
                {t("t_demo")}
              </label>
              <p className="text-[11px] text-muted-foreground">{t("t_demo_hint")}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={!form.name || busy} data-testid="save-tenant-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
